import React from "react";
import { useEffect, useState, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Loader2, Link, Unlink, AlertTriangle, Trash2, Cloud } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useSyncStatus } from '@/hooks/useSyncStatus';
import { useBackgroundSync } from '@/hooks/useBackgroundSync';
import { useAuth } from '@/contexts/AuthContext';
import { useAdAccount } from '@/contexts/AdAccountContext';
import { useUnconfiguredAccounts } from '@/hooks/useUnconfiguredAccounts';
import { supabase } from '@/integrations/supabase/client';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

const Settings = () => {
  const {
    accounts,
    isLoadingAccounts,
    isMetaConnected,
    connectMeta,
    refetchAccounts,
  } = useSyncStatus();
  const [searchParams, setSearchParams] = useSearchParams();
  const { checkMetaConnection, user } = useAuth();
  const { setSelectedAdAccount } = useAdAccount();
  const { data: unconfiguredAccounts = [] } = useUnconfiguredAccounts();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { triggerBackgroundSync } = useBackgroundSync();

  const [removeAccount, setRemoveAccount] = useState<{ id: string; ad_account_id: string; name: string } | null>(null);
  const [isRemoving, setIsRemoving] = useState(false);
  const [quickStartingId, setQuickStartingId] = useState<string | null>(null);

  const handleQuickStart = async (account: { id: string; ad_account_id: string; ad_account_name: string; currency: string | null; timezone: string | null }) => {
    if (!user?.id) return;
    setQuickStartingId(account.id);
    try {
      // Update setup_status to completed
      const { error: updateError } = await supabase
        .from('ad_accounts')
        .update({ setup_status: 'completed' as any })
        .eq('id', account.id)
        .eq('user_id', user.id);
      if (updateError) throw updateError;

      // Upsert minimal ad_account_settings row
      const { error: settingsError } = await supabase
        .from('ad_account_settings')
        .upsert({
          user_id: user.id,
          ad_account_id: account.ad_account_id,
          onboarding_completed: true,
          onboarding_completed_at: new Date().toISOString(),
          step_profiles_completed: true,
          step_tracking_completed: true,
          step_naming_completed: true,
          step_launch_completed: true,
          step_enhancements_completed: true,
        }, { onConflict: 'user_id,ad_account_id' });
      if (settingsError) throw settingsError;

      queryClient.setQueryData(['ad-account-row', user.id, account.ad_account_id], { setup_status: 'completed' });
      setSelectedAdAccount(account.ad_account_id, account.ad_account_name);

      // Pre-fetch Meta data so dropdowns are ready when user opens an ad sheet
      triggerBackgroundSync(user.id, account.ad_account_id);

      await queryClient.invalidateQueries({ queryKey: ['unconfigured-accounts', user.id], refetchType: 'active' });
      await queryClient.invalidateQueries({ queryKey: ['ad-account-row', user.id, account.ad_account_id], refetchType: 'active' });
      await queryClient.invalidateQueries({ queryKey: ['ad-account-list', user.id], refetchType: 'active' });
      await queryClient.invalidateQueries({ queryKey: ['ad-account-statuses', user.id], refetchType: 'active' });
      await refetchAccounts();
      toast.success('Ad account ready!');
    } catch (err) {
      console.error('Quick start failed:', err);
      toast.error('Failed to set up account');
    } finally {
      setQuickStartingId(null);
    }
  };

  // Google Drive state
  const [driveEmail, setDriveEmail] = useState<string | null>(null);
  const [isDriveLoading, setIsDriveLoading] = useState(true);
  const [isDriveConnecting, setIsDriveConnecting] = useState(false);
  const [isDriveDisconnecting, setIsDriveDisconnecting] = useState(false);

  const checkDriveConnection = useCallback(async () => {
    if (!user?.id) return;
    setIsDriveLoading(true);
    try {
      const { data } = await supabase
        .from("drive_oauth_tokens")
        .select("google_email, created_at")
        .eq("user_id", user.id)
        .maybeSingle();
      setDriveEmail(data?.google_email ?? null);
    } catch {
      setDriveEmail(null);
    } finally {
      setIsDriveLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    checkDriveConnection();
  }, [checkDriveConnection]);

  useEffect(() => {
    if (searchParams.get('meta_connected') === 'true' && user?.id) {
      checkMetaConnection(user.id);
      searchParams.delete('meta_connected');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, user?.id, checkMetaConnection, setSearchParams]);

  // Google Drive OAuth callback handling
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("drive_connected") === "true") {
      toast.success("Google Drive connected successfully!");
      window.history.replaceState({}, "", "/settings");
      checkDriveConnection();
    }
    const driveError = params.get("drive_error");
    if (driveError) {
      const errorMessages: Record<string, string> = {
        access_denied: "Google Drive access was denied",
        token_exchange_failed: "Failed to exchange authorization code",
        missing_tokens: "Google did not return the required tokens",
        storage_failed: "Failed to save connection",
        invalid_state: "Security validation failed, please try again",
        missing_params: "Missing parameters in callback",
        internal: "An unexpected error occurred",
      };
      toast.error(errorMessages[driveError] || "Failed to connect Google Drive");
      window.history.replaceState({}, "", "/settings");
    }
  }, []);

  const connectGoogleDrive = async () => {
    setIsDriveConnecting(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("Not authenticated");

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/drive-oauth-callback`,
        {
          method: "POST",
          headers: {
            "Authorization": `Bearer ${session.access_token}`,
            "Content-Type": "application/json",
          },
        }
      );

      const result = await response.json();
      if (result.ok && result.auth_url) {
        window.location.href = result.auth_url;
      } else {
        toast.error("Failed to initiate Google Drive connection");
      }
    } catch (error) {
      console.error("Drive connect error:", error);
      toast.error("Failed to connect Google Drive");
    } finally {
      setIsDriveConnecting(false);
    }
  };

  const disconnectGoogleDrive = async () => {
    if (!user?.id) return;
    setIsDriveDisconnecting(true);
    try {
      const { error } = await supabase
        .from("drive_oauth_tokens")
        .delete()
        .eq("user_id", user.id);
      if (error) throw error;
      setDriveEmail(null);
      toast.success("Google Drive disconnected");
    } catch (error) {
      console.error("Drive disconnect error:", error);
      toast.error("Failed to disconnect Google Drive");
    } finally {
      setIsDriveDisconnecting(false);
    }
  };

  const configuredAccounts = accounts.filter(a => a.setup_status === 'completed');

  const handleRemoveAccount = async () => {
    if (!removeAccount || !user?.id) return;
    setIsRemoving(true);
    try {
      // Reset setup_status to unconfigured
      const { error: updateError } = await supabase
        .from('ad_accounts')
        .update({ setup_status: 'unconfigured' })
        .eq('id', removeAccount.id);
      if (updateError) throw updateError;

      // Delete ad_account_settings
      const { error: deleteError } = await supabase
        .from('ad_account_settings')
        .delete()
        .eq('ad_account_id', removeAccount.ad_account_id)
        .eq('user_id', user.id);
      if (deleteError) throw deleteError;

      await queryClient.invalidateQueries({ queryKey: ['unconfigured-accounts'], refetchType: 'active' });
      await queryClient.invalidateQueries({ queryKey: ['ad-account-row'], refetchType: 'active' });
      await queryClient.invalidateQueries({ queryKey: ['ad-account-list'], refetchType: 'active' });
      await queryClient.invalidateQueries({ queryKey: ['ad-account-statuses'], refetchType: 'active' });
      await refetchAccounts();

      toast.success(`${removeAccount.name} has been removed and will need to be set up again.`);
    } catch (error) {
      console.error('Error removing account:', error);
      toast.error('Failed to remove account configuration.');
    } finally {
      setIsRemoving(false);
      setRemoveAccount(null);
    }
  };

  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-bold text-foreground">Settings</h1>

      {/* Meta Account Connection */}
      <div className="rounded-lg border border-border bg-card p-6">
        <h2 className="mb-4 text-lg font-semibold text-card-foreground">Meta Account Connection</h2>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {isMetaConnected ? (
              <>
                <Link className="h-5 w-5 text-green-500" />
                <div>
                  <p className="font-medium text-card-foreground">Connected</p>
                  <p className="text-sm text-muted-foreground">
                    Your Meta account is linked. Ad accounts are synced automatically.
                  </p>
                </div>
              </>
            ) : (
              <>
                <Unlink className="h-5 w-5 text-muted-foreground" />
                <div>
                  <p className="font-medium text-card-foreground">Not Connected</p>
                  <p className="text-sm text-muted-foreground">
                    Connect your Meta account to start managing ads.
                  </p>
                </div>
              </>
            )}
          </div>
          <Button
            onClick={connectMeta}
            className={isMetaConnected
              ? 'bg-muted text-muted-foreground hover:bg-muted/80'
              : 'bg-[#1877F2] hover:bg-[#1567D8] text-white'}
          >
            {isMetaConnected ? 'Reconnect' : 'Connect Meta Account'}
          </Button>
        </div>
      </div>

      {/* Google Drive Connection */}
      <div className="rounded-lg border border-border bg-card p-6">
        <h2 className="mb-4 text-lg font-semibold text-card-foreground">Google Drive</h2>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {isDriveLoading ? (
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            ) : driveEmail ? (
              <>
                <Cloud className="h-5 w-5 text-green-500" />
                <div>
                  <p className="font-medium text-green-600">Connected as: {driveEmail}</p>
                  <p className="text-sm text-muted-foreground">
                    Your Google Drive is linked for creative uploads.
                  </p>
                </div>
              </>
            ) : (
              <>
                <Cloud className="h-5 w-5 text-muted-foreground" />
                <div>
                  <p className="font-medium text-card-foreground">Not Connected</p>
                  <p className="text-sm text-muted-foreground">
                    Connect your Google Drive to upload creatives directly to Meta ad accounts.
                  </p>
                </div>
              </>
            )}
          </div>
          {!isDriveLoading && (
            driveEmail ? (
              <Button
                variant="destructive"
                onClick={disconnectGoogleDrive}
                disabled={isDriveDisconnecting}
              >
                {isDriveDisconnecting ? 'Disconnecting...' : 'Disconnect'}
              </Button>
            ) : (
              <Button
                onClick={connectGoogleDrive}
                disabled={isDriveConnecting}
                className="bg-[#1877F2] hover:bg-[#1567D8] text-white"
              >
                {isDriveConnecting ? 'Connecting...' : 'Connect Google Drive'}
              </Button>
            )
          )}
        </div>
      </div>

      {/* Connected Ad Accounts (completed only) */}
      <div className="rounded-lg border border-border bg-card p-6">
        <h2 className="mb-4 text-lg font-semibold text-card-foreground">Connected Ad Accounts</h2>

        {isLoadingAccounts ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : configuredAccounts.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No configured ad accounts. Set up an account below to get started.
          </p>
        ) : (
          <div className="rounded-md border border-border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Account Name</TableHead>
                  <TableHead>Account ID</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Currency</TableHead>
                  <TableHead>Timezone</TableHead>
                  <TableHead className="w-[80px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {configuredAccounts.map((account) => (
                  <TableRow key={account.id}>
                    <TableCell className="font-medium">
                      {account.display_name || account.ad_account_name}
                    </TableCell>
                    <TableCell>{account.ad_account_id}</TableCell>
                    <TableCell>
                      <Badge variant={account.status === 'ACTIVE' || !account.status ? 'default' : 'secondary'}>
                        {account.status || 'ACTIVE'}
                      </Badge>
                    </TableCell>
                    <TableCell>{account.currency || '-'}</TableCell>
                    <TableCell>{account.timezone || '-'}</TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-muted-foreground hover:text-destructive"
                        onClick={() => setRemoveAccount({
                          id: account.id,
                          ad_account_id: account.ad_account_id,
                          name: account.display_name || account.ad_account_name,
                        })}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* Ad Accounts not Configured */}
      {unconfiguredAccounts.length > 0 && (
        <Card className="border-l-4 border-l-yellow-500">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <AlertTriangle className="h-4 w-4 text-yellow-600" />
              Ad Accounts not Configured
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {unconfiguredAccounts.map((account) => (
              <div
                key={account.id}
                className="flex items-center justify-between rounded-md border border-border bg-muted/30 px-4 py-3"
              >
                <div className="flex items-center gap-3">
                  <div>
                    <p className="text-sm font-medium text-foreground">{account.ad_account_name}</p>
                    <p className="text-xs text-muted-foreground">{account.ad_account_id}</p>
                  </div>
                  {account.currency && (
                    <Badge variant="outline" className="text-xs">{account.currency}</Badge>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    onClick={() => handleQuickStart(account)}
                    disabled={quickStartingId === account.id}
                  >
                    {quickStartingId === account.id ? 'Setting up...' : 'Quick Start'}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      // Pre-fetch Meta data so dropdowns are ready after setup
                      if (user?.id) triggerBackgroundSync(user.id, account.ad_account_id);
                      navigate(`/setup/account/${account.ad_account_id}`);
                    }}
                  >
                    {account.setup_status === 'in_progress' ? 'Continue Setup' : 'Custom Setup'}
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Remove Confirmation Dialog */}
      <AlertDialog open={!!removeAccount} onOpenChange={(open) => !open && setRemoveAccount(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {removeAccount?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This will reset the account configuration. You'll need to set it up again to use it.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isRemoving}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRemoveAccount}
              disabled={isRemoving}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isRemoving ? 'Removing...' : 'Remove'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Settings;
